import { useCallback, useEffect, useState } from 'react';
import sessionsApi from 'Api/axios/sessions-api';

/**
 * Owns the evidences list for a session.
 *
 * Exposes:
 *   - `evidences`, `loading`, `error`, `uploadProgress`
 *   - `fetchEvidences()` — refetch
 *   - `uploadEvidence(file)` — multipart upload with progress
 *   - `deleteEvidence(evidenceId)` — soft delete + S3 cleanup
 */
export default function useEvidencesApi({ sessionId, init = true } = {}) {
  const [evidences, setEvidences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);

  const fetchEvidences = useCallback(async () => {
    if (!sessionId) return { data: [], error: null };
    setLoading(true);
    setError(null);
    const response = await sessionsApi.listEvidences(sessionId, { limit: 50 });
    if (response.error) {
      setError(response.error);
      setEvidences([]);
    } else {
      setEvidences(response.data || []);
    }
    setLoading(false);
    return response;
  }, [sessionId]);

  useEffect(() => {
    if (init && sessionId) fetchEvidences();
  }, [sessionId, init, fetchEvidences]);

  const uploadEvidence = useCallback(
    async file => {
      if (!sessionId) return { data: null, error: 'No session' };
      setUploadProgress({ name: file.name, percent: 0 });
      const response = await sessionsApi.uploadEvidence(sessionId, file, evt => {
        const total = evt.total || file.size;
        const percent = total ? Math.round((evt.loaded * 100) / total) : 0;
        setUploadProgress({ name: file.name, percent });
      });
      setUploadProgress(null);
      return response;
    },
    [sessionId]
  );

  const deleteEvidence = useCallback(
    async evidenceId => sessionsApi.deleteEvidence(sessionId, evidenceId),
    [sessionId]
  );

  return {
    evidences,
    loading,
    error,
    uploadProgress,
    fetchEvidences,
    uploadEvidence,
    deleteEvidence,
  };
}
